'use client';

import { Spinner, TagFilter, Typography } from '@workspace/ui/common';
import { ALGOLIA_CONFIG, cn } from '@/app/lib';
import { Section } from '@workspace/interfaces';
import { InstantSearchProvider } from '@/app/providers';
import { AlgoliaClearRefinements, AlgoliaMenuSelect, AlgoliaSearchBox, DocumentHubLayout } from '@workspace/widgets';
import { AlgoliaDocumentHub } from './DocumentHub';
import { useCallback, useEffect, useMemo, useState } from 'react';

type SearchBarData = { introText?: string; placeholder?: string } | null | undefined;
type FilterBarData = { sectionTitle?: string; enabledFilters?: string[] | null } | null | undefined;

interface ClientDocumentHubProps {
  className: string;
  sections: Section[];
  pageSlug: string;
  pageTags: { id: string; label: string }[];
  searchBar: SearchBarData;
  filterBar: FilterBarData;
  downloadCTA?: string;
  viewCTA?: string;
}

export function ClientDocumentHubPage({
  className,
  sections,
  pageSlug,
  pageTags,
  searchBar,
  filterBar,
  downloadCTA,
  viewCTA,
}: ClientDocumentHubProps) {
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(['all']);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setIsReady(true);
  }, []);

  const filterActive = useMemo(() => !selectedTagIds.includes('all'), [selectedTagIds]);
  const tagItems = useMemo(
    () =>
      [{ id: 'all', label: 'All Documents' }, ...pageTags].map((t) => ({
        ...t,
        isSelected: selectedTagIds.includes(t.id),
      })),
    [pageTags, selectedTagIds],
  );

  const onResetTags = useCallback(() => {
    setSelectedTagIds(['all']);
  }, []);

  const introText = searchBar?.introText || 'You can quickly find your document here';
  const placeholder = searchBar?.placeholder || 'Search by product, file type, or keyword…';
  const filterTitle = filterBar?.sectionTitle || 'Filter Documents';

  const handleTagChange = useCallback((ids: string[]) => {
    setSelectedTagIds(ids.length ? ids : ['all']);
  }, []);

  if (!isReady) {
    return (
      <div className={cn('wrapper lg:py-3xl px-md', className)}>
        <div className="flex items-center justify-center py-16">
          <Spinner size="large" className="text-primary" />
        </div>
      </div>
    );
  }

  return (
    <InstantSearchProvider indexName={ALGOLIA_CONFIG.LIST_INSTANT_SEARCH_INDEX.Documents}>
      <DocumentHubLayout className={className} pageSlug={pageSlug}>
        {/* Search and Filter Section */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-6 pt-8 md:pt-0">
          {/* Search Section */}
          <div className="flex-1 md:max-w-122">
            <Typography variant="bodyMd" className="mb-4 text-black font-semibold">
              {introText}
            </Typography>
            <AlgoliaSearchBox placeholder={placeholder} />
          </div>

          {/* Filter Section */}
          {filterBar?.enabledFilters && filterBar?.enabledFilters?.length && (
            <div className="flex flex-col w-full md:max-w-104">
              <Typography variant="bodyMd" className="mb-4 text-black font-semibold">
                {filterTitle}
              </Typography>
              <div className="grid grid-cols-2 gap-3 md:flex md:gap-4 justify-between flex-wrap w-full">
                {filterBar?.enabledFilters?.includes('productGroups') && (
                  <AlgoliaMenuSelect menuProps={{ attribute: 'productGroups.name' }} placeholder="Product Groups" />
                )}
                {filterBar?.enabledFilters?.includes('documentTags') && (
                  <AlgoliaMenuSelect menuProps={{ attribute: 'documentTags.name' }} placeholder="Document Tags" />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Tag Filter Section */}
        <TagFilter className="pt-10 md:py-sm md:mt-lg" tags={tagItems} multiSelect onChangeSelected={handleTagChange} />
        <AlgoliaClearRefinements overrideDisabled={!filterActive} onClick={onResetTags} excludedAttributes={[]} />

        <AlgoliaDocumentHub
          sections={sections}
          pageSlug={pageSlug}
          downloadCTA={downloadCTA}
          viewCTA={viewCTA}
          filterActive={filterActive}
          selectedTagIds={selectedTagIds}
          onTagsDiscovered={undefined}
        />
      </DocumentHubLayout>
    </InstantSearchProvider>
  );
}
